import type { HTMLAttributes, ReactNode } from 'react';
import { Add, Filter } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import styles from './FilterBar.module.css';

export interface FilterBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Usually a <SearchInput />. Takes the remaining width. */
  search?: ReactNode;
  /** Filter controls (Selects, Menus, ContextSelectors...). Pass as children. */
  children?: ReactNode;
  /** Active filter chips shown on a second row. */
  chips?: ReactNode;
  /** Right-aligned actions (export, column settings...). */
  actions?: ReactNode;
  /** Shows a "Clear filters" action when provided and `canClear` is true. */
  onClearFilters?: () => void;
  canClear?: boolean;
  clearLabel?: string;
  /** Accessible name for the filter group. */
  label?: string;
  /**
   * `card` (default): its own panel, placed above the table's card.
   * `inset`: a shaded band inside a card that has its own title (e.g. a tab's list card).
   */
  variant?: 'card' | 'inset';
}

export function FilterBar({
  search,
  children,
  chips,
  actions,
  onClearFilters,
  canClear = true,
  clearLabel = 'Clear filters',
  label = 'Filters',
  variant = 'card',
  className,
  ...rest
}: FilterBarProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(styles.bar, styles[variant], className)}
      {...rest}
    >
      <div className={styles.row}>
        <Filter
          size={20}
          color="currentColor"
          variant="Bulk"
          className={styles.icon}
          aria-hidden="true"
        />
        {search && <div className={styles.search}>{search}</div>}
        {children && <div className={styles.filters}>{children}</div>}
        {onClearFilters && canClear && (
          <button type="button" className={styles.clear} onClick={onClearFilters}>
            {clearLabel}
          </button>
        )}
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {chips && (
        <ul className={styles.chips} aria-label="Active filters">
          {chips}
        </ul>
      )}
    </div>
  );
}

export interface FilterChipProps extends Omit<HTMLAttributes<HTMLLIElement>, 'children'> {
  /** Filter name, e.g. "Grade". */
  label: string;
  /** Filter value, e.g. "5". */
  value: ReactNode;
  /** Plain-text value for the remove button's accessible name. */
  valueText?: string;
  onRemove: () => void;
}

/** Removable active filter. Render inside FilterBar `chips` (it is an <li>). */
export function FilterChip({
  label,
  value,
  valueText,
  onRemove,
  className,
  ...rest
}: FilterChipProps) {
  const text =
    valueText ?? (typeof value === 'string' || typeof value === 'number' ? String(value) : '');
  return (
    <li className={cx(styles.chip, className)} {...rest}>
      <span className={styles.chipText}>
        <span className={styles.chipLabel}>{label}:</span> <span>{value}</span>
      </span>
      <button
        type="button"
        className={styles.chipRemove}
        aria-label={`Remove filter ${label}${text ? `: ${text}` : ''}`}
        onClick={onRemove}
      >
        <Add size={16} color="currentColor" className={styles.chipIcon} aria-hidden="true" />
      </button>
    </li>
  );
}
